/**
 * First-touch `utm_campaign` for the current browser tab. Survives OAuth and SPA
 * navigations so GTM can attribute {@link ../oauthSuccessAppLoadGtm} to marketing
 * entry (e.g. Webflow TRY FOR FREE → `?utm_campaign=try_for_free`).
 *
 * Captured once per tab from the **first** URL query string; not overwritten on
 * later route changes.
 */

const SESSION_KEY = "bs_app_first_touch_utm_campaign";

/**
 * Most-recently captured Reddit click ID (`rdt_cid`). Updated whenever a
 * landing URL carries a non-empty `rdt_cid`; persisted in localStorage so it
 * survives SPA navigations, full-page OAuth bounces, and new tabs in the same
 * browser profile. Threaded onto the OAuth redirect so the backend can persist
 * it on the user record (used for Reddit CAPI SignUp/Purchase attribution).
 */
const RDT_CID_KEY = "bs_app_first_touch_rdt_cid";

/** Recommended `utm_campaign` for the Webflow TRY FOR FREE button (align with GTM). */
export const WEBFLOW_TRY_FOR_FREE_UTM_CAMPAIGN = "try_for_free";

export function captureAppFirstTouchUtmCampaignFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (window.sessionStorage.getItem(SESSION_KEY) !== null) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("utm_campaign");
    const campaign = raw && raw.trim() !== "" ? raw.trim().slice(0, 256) : "";
    window.sessionStorage.setItem(SESSION_KEY, campaign);
  } catch {
    // ignore
  }
}

/**
 * Capture `rdt_cid` from the current URL when it is present.
 *
 * Updates the stored value whenever the current URL carries a non-empty
 * `rdt_cid`, so a second Reddit ad click in the same browser correctly
 * replaces a stale click ID. Pages that do NOT carry the param are ignored,
 * leaving the previously captured value intact — this is what lets the value
 * survive SPA navigations that strip query params.
 */
export function captureAppFirstTouchRdtCidFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("rdt_cid");
    if (raw && raw.trim() !== "") {
      window.localStorage.setItem(RDT_CID_KEY, raw.trim().slice(0, 512));
    }
  } catch {
    // ignore
  }
}

/**
 * Most-recently captured `rdt_cid` for this browser profile, or null if none
 * has been captured. Persists in `localStorage` (not `sessionStorage`) so it
 * survives the full-page GitHub OAuth redirect.
 */
export function getAppFirstTouchRdtCid(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const v = window.localStorage.getItem(RDT_CID_KEY);
    return v && v !== "" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Clear the persisted `rdt_cid` after a successful signup so it cannot be
 * reused to mis-attribute a future signup on the same browser (e.g. a shared
 * computer or a second account). Called once the `bs_new_github_signup` query
 * param is detected, which is the moment the backend confirms the new account.
 */
export function clearRdtCid(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(RDT_CID_KEY);
  } catch {
    // ignore
  }
}

/**
 * Append the persisted first-touch `rdt_cid` to an OAuth redirect URL when the
 * URL does not already carry one, so it survives to the backend even if the
 * sign-in click happened on a clean URL after the original ad landing.
 */
export function withFirstTouchRdtCid(redirectUrl: string): string {
  const stored = getAppFirstTouchRdtCid();
  if (!stored) {
    return redirectUrl;
  }
  try {
    const url = new URL(redirectUrl, window.location.origin);
    if (!url.searchParams.has("rdt_cid")) {
      url.searchParams.set("rdt_cid", stored);
    }
    return url.toString();
  } catch {
    return redirectUrl;
  }
}

/**
 * Value for dataLayer `entry_utm_campaign`: real campaign or `(none)` if the first
 * landing URL had no `utm_campaign`.
 */
export function getAppFirstTouchUtmCampaignForGtm(): string {
  if (typeof window === "undefined") {
    return "(none)";
  }
  try {
    const v = window.sessionStorage.getItem(SESSION_KEY);
    if (v === null || v === "") {
      return "(none)";
    }
    return v;
  } catch {
    return "(none)";
  }
}
